// AutoCompleteContaBancariaSimples.tsx
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../../shared/hooks';
import { ContaBancariaService } from '../services/api/Venda/ContaBancariaService';

type TAutoCompleteOption = {
  idconta: string;
  label: string;
}

interface IAutoCompleteContaBancariaSimpleProps {
  isExternalLoading?: boolean;
  label: string;
  value?: string;
  onChange?: (idconta: string, label?: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  // Filtra pelo tipo da conta financeira (ex.: 'banco') — sem isso, lista
  // contas de qualquer tipo (caixa/cofre/recebíveis inclusive), o que não
  // faz sentido pra escolher a conta de destino de um PIX Estático.
  tipo?: string;
  // Remove essa conta da lista de opções (ex.: o próprio caixa do operador
  // logado, na transferência entre caixas — o backend já recusa transferir
  // pra si mesmo, mas sem isso a conta aparecia normalmente na busca e só
  // dava erro depois de escolhida).
  excludeId?: string;
}

export const AutoCompleteContaBancariaSimple: React.FC<IAutoCompleteContaBancariaSimpleProps> = ({
  isExternalLoading = false,
  label,
  value,
  onChange,
  error = false,
  helperText = '',
  disabled = false,
  inputRef,
  onKeyDown,
  tipo,
  excludeId,
}) => {
  const { debounce } = useDebounce();
  const [selectedId, setSelectedId] = useState<string | undefined>(value);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  // Sincronizar com o valor externo
  useEffect(() => {
    setSelectedId(value);
  }, [value]);

  // Buscar opções
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, selectedId?.toString(), tipo)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setOpcoes([]);
          } else {
            setOpcoes(
              result.data
                .filter(conta => conta.idconta !== excludeId)
                .map(conta => ({ idconta: conta.idconta, label: `${conta.numeroconta} - ${conta.titular}` }))
            );
          }
        });
    });
  }, [busca, selectedId, tipo, excludeId, debounce]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;
    const selectedOption = opcoes.find(opcao => opcao.idconta === selectedId);
    return selectedOption || null;
  }, [selectedId, opcoes]);

  // Tipo correto para o evento do Autocomplete do MUI
  const handleChange = (_event: React.SyntheticEvent, newValue: TAutoCompleteOption | null) => {
    const newId = newValue?.idconta || '';
    setSelectedId(newId);
    onChange?.(newId, newValue?.label);
    setBusca('');
  };

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'
      disablePortal
      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading || disabled}
      value={autoCompleteSelectedOption}
      onInputChange={(_event: React.SyntheticEvent, newValue: string) => setBusca(newValue)}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label={label}
          error={error}
          helperText={helperText}
          inputRef={inputRef}
        />
      )}
    />
  );
};